import { useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { ScrollContainer } from 'react-indiana-drag-scroll'

import 'react-indiana-drag-scroll/dist/style.css'

import { breakpoints, colors } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import {
  AccountType,
  Plan,
  planList,
  PricingPlans
} from '@/features/Pricing/pricing-plans'
import { PricingButton } from '@/features/Pricing/types'
import PricingCard from '@/components/pricing-card'
import { ParagraphRegular } from '@/components/text'

import { SectionHeading } from './elements'

type PlanTypes = 'essentials' | 'growth' | 'business'

const PricingCardsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 -32px 66px !important;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 -32px 36px !important;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    margin: 0 -12px 64px !important;
  }
`

const ScrollWrapper = styled.div`
  width: 100%;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: start;
  grid-gap: 32px;
  cursor: default;
  width: 100%;
  padding: 4px 32px 20px;

  @media (max-width: ${breakpoints.v3Desktop}) {
    grid-gap: 24px;
  }

  @media (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: repeat(4, 1fr);
    // Workaround for bug where grid inside scrolling div doesn't respect padding
    :after {
      height: 1px;
      width: 12px;
      content: '';
    }
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: unset;
    justify-content: center;
    padding: 4px 12px 20px;

    :after {
      content: none;
    }
  }
`

const CardWrapper = styled.div<{ type: PlanTypes }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 565px;
  height: 100%;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    width: 415px;
    min-height: unset;
    height: unset;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    width: unset;
    max-width: 415px;
    ${props => props.type === 'essentials' && `order: -1;`}
  }
`

const FooterText = styled(ParagraphRegular)`
  margin-top: 28px;

  a {
    text-decoration: none;
    color: ${colors.link};
  }
`

type PricingCardsProps = {
  plansData: PricingPlans
  hideButtons?: boolean
  featuredPlan?: AccountType
  featuredText?: string
  footerText?: React.ReactNode
  getButtonData: (plan: Plan, condensed: boolean) => PricingButton
}

const PricingCards = ({
  plansData,
  hideButtons,
  featuredPlan,
  featuredText,
  getButtonData,
  footerText
}: PricingCardsProps) => {
  const { t } = useTranslation('pricing')
  const displayTablet = useMediaQuery(
    `(max-width: ${breakpoints.tabletLandscape})`
  )
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const plans = useMemo(() => {
    if (displayTablet && featuredPlan) {
      const updatedPlansList = [
        featuredPlan,
        ...planList.filter(item => item !== featuredPlan)
      ]
      return updatedPlansList
    }
    return planList
  }, [featuredPlan, displayTablet])

  return (
    <PricingCardsSection>
      <SectionHeading as="h1">
        {t('pricing:Landlords, choose the best plan for your needs')}
      </SectionHeading>
      <ScrollWrapper>
        <ScrollContainer>
          <Grid>
            {plans.map(plan => {
              const data = plansData[plan]
              const isFeaturedPlan = data.value === featuredPlan
              const buttonData = getButtonData(data, false)
              return (
                <CardWrapper key={plan} type={plan}>
                  <PricingCard
                    name={data.name}
                    value={data.value}
                    description={data.description}
                    price={data.price}
                    features={data.features}
                    buttonText={buttonData.text}
                    trackingId={`${data.id}-main`}
                    top={!isFeaturedPlan && !displayMobile ? 32 : 0}
                    featuredText={isFeaturedPlan ? featuredText : undefined}
                    hideButtons={hideButtons}
                    disableButton={buttonData.disabled}
                    href={buttonData.href}
                    onClick={buttonData.onClick}
                  />
                </CardWrapper>
              )
            })}
          </Grid>
        </ScrollContainer>
      </ScrollWrapper>
      {footerText && <FooterText>{footerText}</FooterText>}
    </PricingCardsSection>
  )
}

export default PricingCards
